import React from 'react';

const money = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;

const dateParts = (value) => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  return {
    date: date.toLocaleDateString('en-GB'),
    time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
  };
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  fontSize: '11px',
};

const dividerStyle = {
  borderTop: '1px dashed #111',
  margin: '8px 0',
};

const tableHeaderCell = {
  fontSize: '8px',
  fontWeight: 700,
  paddingBottom: '3px',
  borderBottom: '1px solid #D1D5DB',
};

const gstHeaderCell = {
  fontSize: '7.5px',
  fontWeight: 700,
  paddingBottom: '3px',
  borderBottom: '1px solid #D1D5DB',
};

// Font size is a receipt-template setting applied to this on-screen preview
// and the PDF only -- not the raw ESC/POS thermal print path (see
// buildEscposLines.js). `zoom` scales the whole rendered receipt uniformly
// without needing to hand-multiply every hardcoded px value in this file.
const FONT_SCALE = { small: 0.9, normal: 1, large: 1.15 };
// 80mm roll paper only has ~72mm of printable width (576 dots @ 203dpi);
// anything wider is clipped on the right. Divide by zoom so the scaled
// receipt still lands at exactly the printable width.
const PRINTABLE_WIDTH_MM = 72;

const ThermalReceipt80mm = ({ receipt }) => {
  const { date, time } = dateParts(receipt?.createdAt);
  const showRate = receipt?.showRateColumn !== false;
  const showAmt = receipt?.showAmountColumn !== false;
  const decimals = receipt?.itemAmountDecimalPlaces || 2;
  const rateLabel = receipt?.rateColumnLabel || 'Rate';
  const amtLabel = receipt?.amountColumnLabel || 'Amt';
  const zoom = FONT_SCALE[receipt?.receiptFontSize] ?? FONT_SCALE.normal;
  // Hiding a column returns its width to Item rather than leaving blank
  // gutter -- 18%/18% (Rate/Amt) folded back into Item's base 40% as needed.
  const itemColWidth = `${40 + (showRate ? 0 : 18) + (showAmt ? 0 : 18)}%`;

  return (
    <div style={{ width: `${PRINTABLE_WIDTH_MM / zoom}mm`, color: '#111', fontFamily: 'monospace', padding: '8px 4px', boxSizing: 'border-box', zoom }}>
      <div style={{ textAlign: 'center' }}>
        {receipt.logoUrl && (
          <img
            src={receipt.logoUrl}
            alt=""
            style={{ maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', marginBottom: '4px', filter: 'grayscale(1) contrast(1.3)' }}
          />
        )}
        <div style={{ fontSize: '18px', fontWeight: 700 }}>{receipt.storeName}</div>
        {receipt.organizationName && <div style={{ fontSize: '12px', marginTop: '4px' }}>{receipt.organizationName}</div>}
        {receipt.storeAddress && <div style={{ fontSize: '11px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{receipt.storeAddress}</div>}
        {receipt.storePhone && <div style={{ fontSize: '11px' }}>Mobile: {receipt.storePhone}</div>}
        {receipt.storeEmail && <div style={{ fontSize: '11px' }}>Email: {receipt.storeEmail}</div>}
        {receipt.gstin && <div style={{ fontSize: '11px', marginTop: '2px' }}>GST NO.: {receipt.gstin}</div>}
      </div>

      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '15px' }}>{receipt.invoiceTitle}</div>
      <div style={dividerStyle} />

      <div style={{ fontSize: '11px' }}>
        <div style={rowStyle}><span>Bill No</span><span>{receipt.billNumber}</span></div>
        <div style={rowStyle}><span>Date</span><span>{date}</span></div>
        <div style={rowStyle}><span>Time</span><span>{time}</span></div>
        {receipt.cashierName && <div style={rowStyle}><span>Cashier</span><span>{receipt.cashierName}</span></div>}
      </div>

      {(receipt.customerName || receipt.customerMobile || receipt.customerAddress) && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px' }}>
            {receipt.customerName && <div>Customer: {receipt.customerName}</div>}
            {receipt.customerMobile && <div>Mobile: {receipt.customerMobile}</div>}
            {receipt.customerAddress && <div style={{ whiteSpace: 'pre-wrap' }}>Address: {receipt.customerAddress}</div>}
          </div>
        </>
      )}

      <div style={dividerStyle} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
        <thead>
          <tr>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: '8%' }}>Sr</th>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: itemColWidth }}>Item</th>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: '16%' }}>Qty</th>
            {showRate && <th style={{ ...tableHeaderCell, textAlign: 'right', width: '18%' }}>{rateLabel}</th>}
            {showAmt && <th style={{ ...tableHeaderCell, textAlign: 'right', width: '18%' }}>{amtLabel}</th>}
          </tr>
        </thead>
        <tbody>
          {receipt.items.map((item) => (
            <React.Fragment key={`${item.index}-${item.name}`}>
              <tr>
                <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>{item.index}</td>
                <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, lineHeight: 1.3 }}>{item.name}</div>
                  {!receipt.hideHsn && <div style={{ fontSize: '9px' }}>HSN: {item.hsn || '-'}</div>}
                  {(item.color || item.size) && (
                    <div style={{ fontSize: '9px' }}>
                      {[item.color && `Color: ${item.color}`, item.size && `Size: ${item.size}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {item.barcode && <div style={{ fontSize: '9px' }}>Barcode: {item.barcode}</div>}
                  {item.discountAmount > 0 && (
                    item.savingsMessage ? (
                      <div style={{ fontSize: '9px', color: '#166534', fontWeight: 700 }}>{item.savingsMessage}</div>
                    ) : (
                      <div style={{ fontSize: '9px' }}>
                        Discount{item.discountLabel ? ` (${item.discountLabel})` : ''}: {money(item.discountAmount)}
                      </div>
                    )
                  )}
                </td>
                <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>{item.quantity}</td>
                {showRate && <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>{item.rate.toFixed(decimals)}</td>}
                {showAmt && <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>{item.amount.toFixed(decimals)}</td>}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div style={dividerStyle} />
      <div style={{ fontSize: '11px' }}>
        <div style={rowStyle}><span>Subtotal</span><span>{money(receipt.subtotal)}</span></div>
        {receipt.billDiscountAmount > 0 && (
          <div style={rowStyle}>
            <span>Bill Discount{receipt.billDiscountLabel ? ` (${receipt.billDiscountLabel})` : ''}</span>
            <span>{money(receipt.billDiscountAmount)}</span>
          </div>
        )}
        {receipt.billPromotionDiscountAmount > 0 && (
          <div style={rowStyle}>
            <span>Promotion Discount{receipt.billPromotionDiscountLabel ? ` (${receipt.billPromotionDiscountLabel})` : ''}</span>
            <span>{money(receipt.billPromotionDiscountAmount)}</span>
          </div>
        )}
        {receipt.discount > 0 && receipt.billDiscountAmount <= 0 && receipt.billPromotionDiscountAmount <= 0 && (
          receipt.billSavingsMessage ? (
            <div style={{ ...rowStyle, color: '#166534', fontWeight: 700 }}><span>{receipt.billSavingsMessage}</span></div>
          ) : (
            <div style={rowStyle}><span>Discount</span><span>{money(receipt.discount)}</span></div>
          )
        )}
        {receipt.roundingAdjustment !== 0 && (
          <div style={rowStyle}>
            <span>Rounding</span>
            <span>{receipt.roundingAdjustment > 0 ? '+' : '−'}{money(Math.abs(receipt.roundingAdjustment))}</span>
          </div>
        )}
        <div style={{ ...rowStyle, fontWeight: 700 }}><span>Total Amount</span><span>{money(receipt.totalAmount)}</span></div>
      </div>

      {(receipt.payments.length > 0 || receipt.loyaltyRedemption > 0) && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Payments</div>
          {receipt.payments.map((payment) => (
            <div key={`${payment.label}-${payment.referenceNumber}-${payment.amount}`} style={{ marginBottom: '2px' }}>
              <div style={rowStyle}><span>{payment.label}</span><span>{money(payment.amount)}</span></div>
              {payment.referenceNumber && <div style={{ fontSize: '9px' }}>Ref: {payment.referenceNumber}</div>}
              {payment.note && <div style={{ fontSize: '9px' }}>{payment.note}</div>}
            </div>
          ))}
          {receipt.loyaltyRedemption > 0 && (
            <div style={rowStyle}><span>Loyalty Points</span><span>{money(receipt.loyaltyRedemption)}</span></div>
          )}
          {receipt.showTotalPaidRow && (
            <div style={{ ...rowStyle, fontWeight: 700 }}><span>Total Paid</span><span>{money(receipt.totalPaid)}</span></div>
          )}
          {receipt.changeDue > 0 && <div style={rowStyle}><span>Change Due</span><span>{money(receipt.changeDue)}</span></div>}
        </>
      )}

      {(receipt.loyaltyPointsEarned > 0 || receipt.loyaltyRedemption > 0) && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Loyalty Points</div>
          {receipt.loyaltyPointsEarned > 0 && (
            <div style={rowStyle}><span>Points Earned</span><span>+{Math.round(receipt.loyaltyPointsEarned)}</span></div>
          )}
          {receipt.loyaltyPointsBalance != null && (
            <div style={rowStyle}><span>Points Balance</span><span>{Math.round(receipt.loyaltyPointsBalance)}</span></div>
          )}
        </>
      )}

      {receipt.gstSummary.length > 0 && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>GST Summary</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
            <thead>
              <tr>
                <th style={{ ...gstHeaderCell, textAlign: 'left', width: '24%' }}>Tax</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>Taxable</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>CGST</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>SGST</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>IGST</th>
              </tr>
            </thead>
            <tbody>
              {receipt.gstSummary.map((row) => (
                <tr key={`${row.label}-${row.taxableAmount}`}>
                  <td style={{ paddingTop: '4px' }}>{row.label}</td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>{row.taxableAmount.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>{row.cgstAmount.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>{row.sgstAmount.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>{row.igstAmount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {receipt.salespersonName && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px' }}>Salesperson: {receipt.salespersonName}</div>
        </>
      )}

      <div style={dividerStyle} />
      <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Terms &amp; Conditions</div>
      <ol style={{ margin: 0, paddingLeft: '16px', fontSize: '9px', lineHeight: 1.5 }}>
        {receipt.terms.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ol>

      {receipt.declarationText && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Declarations</div>
          <div style={{ fontSize: '9px', lineHeight: 1.5, textAlign: 'justify' }}>{receipt.declarationText}</div>
        </>
      )}

      {receipt.footerMessage && (
        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, marginTop: '10px' }}>
          {receipt.footerMessage}
        </div>
      )}
      {receipt.websiteHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.websiteHandle}</div>
      )}
      {receipt.instagramHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.instagramHandle}</div>
      )}
    </div>
  );
};

export default ThermalReceipt80mm;
